import { useEffect, useMemo, useState } from 'react';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';

import { CONFIG } from '~/config';

import { ExternalRestClient } from '~/modules/requests';

import { Place } from '~/types';

type ReferenceRecord = {
  code?: string;
  name?: string;
  name_translations?: Record<string, string>;
  city_code?: string;
  country_code?: string;
  city_name?: string;
  country_name?: string;
};

type Suggestion = Place & { depth: 0 | 1; optionLabel: string };
type Props = {
  name: string;
  label: string;
  placeholder: string;
  location: Place | null;
  required?: boolean;
  errorHelperText?: string[];
  onLocationChange: (event: React.SyntheticEvent, value: Place | null) => void;
};
const MIN_QUERY_LENGTH = 2;
const cache = new Map<string, Suggestion[]>();
const sanitizeCode = (value?: string) =>
  (value ?? '')
    .replace(/[^\da-z]/gi, '')
    .toUpperCase()
    .slice(0, 8);
const dataFrom = (payload?: { data?: ReferenceRecord[] } | ReferenceRecord[]) =>
  Array.isArray(payload) ? payload : payload?.data ?? [];

async function fetchRecords(
  dataset: 'cities' | 'airports',
  params: Record<string, string | number>,
) {
  const query = new URLSearchParams(
    Object.entries(params).map(([key, value]) => [key, String(value)]),
  ).toString();
  const response = await ExternalRestClient.get<{ data?: ReferenceRecord[] } | ReferenceRecord[]>(
    `${CONFIG.env.apiBaseUrl}v2/reference-data/${dataset}?${query}`,
  );

  return dataFrom(response.data);
}

const mapCity = (record: ReferenceRecord): Suggestion | null => {
  const iata = sanitizeCode(record.code);
  const city = (record.name_translations?.en || record.name || iata).trim();
  const country = (record.country_name || sanitizeCode(record.country_code)).trim();

  if (!iata || !city) {
    return null;
  }

  const title = [city, country].filter(Boolean).join(', ');

  return { city, country, depth: 0, iata, optionLabel: `${title} (${iata})`, title, type: 'city' };
};

const mapAirport = (record: ReferenceRecord, depth: 0 | 1): Suggestion | null => {
  const iata = sanitizeCode(record.code);
  const title = (record.name_translations?.en || record.name || iata).trim();
  const city = (record.city_name || sanitizeCode(record.city_code)).trim();
  const country = (record.country_name || sanitizeCode(record.country_code)).trim();

  if (!iata || !title) {
    return null;
  }

  const context = [city, country].filter(Boolean).join(', ');

  return {
    city,
    country,
    depth,
    iata,
    optionLabel: context ? `${title} (${iata}) · ${context}` : `${title} (${iata})`,
    title,
    type: 'airport',
  };
};

async function fetchSuggestions(query: string) {
  const normalized = query.trim().toLowerCase();

  if (normalized.length < MIN_QUERY_LENGTH) {
    return [];
  }

  if (cache.has(normalized)) {
    return cache.get(normalized) ?? [];
  }

  const [cityRecords, airportRecords] = await Promise.all([
    fetchRecords('cities', { search: normalized, limit: 6 }),
    fetchRecords('airports', { search: normalized, limit: 8, flightable: 1, civilOnly: 1 }),
  ]);
  const cities = cityRecords.map(mapCity).filter((value): value is Suggestion => Boolean(value));
  const groups = await Promise.all(
    cities.map(async city => {
      const records = await fetchRecords('airports', {
        cityCode: city.iata,
        limit: 50,
        flightable: 1,
        civilOnly: 1,
      });

      return records
        .map(record => mapAirport(record, 1))
        .filter((value): value is Suggestion => Boolean(value));
    }),
  );
  const groupedCodes = new Set(groups.flat().map(airport => airport.iata));
  const directAirports = airportRecords
    .map(record => mapAirport(record, 0))
    .filter((value): value is Suggestion => value !== null)
    .filter(value => !groupedCodes.has(value.iata));
  const suggestions = [
    ...cities.flatMap((city, index) => [city, ...groups[index]]),
    ...directAirports,
  ];

  cache.set(normalized, suggestions);

  return suggestions;
}

function ItineraryLocationSelector({
  errorHelperText,
  label,
  location,
  name,
  onLocationChange,
  placeholder,
  required,
}: Props) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const selected = useMemo(
    () =>
      location
        ? { ...location, depth: 0 as const, optionLabel: `${location.title} (${location.iata})` }
        : null,
    [location],
  );
  const selectedLabel = selected ? `${selected.title} (${selected.iata})` : '';

  useEffect(() => {
    setInputValue(selectedLabel);
  }, [selectedLabel]);

  const handleLocationChange = (event: React.SyntheticEvent, value: Suggestion | null) => {
    setInputValue(value ? `${value.title} (${value.iata})` : '');
    onLocationChange(event, value);
  };

  const handleInputChange = (
    _: React.SyntheticEvent,
    value: string,
    reason: 'input' | 'reset' | 'clear',
  ) => {
    if (reason === 'input' || reason === 'clear') {
      setInputValue(value);

      return;
    }

    setInputValue(selectedLabel || value);
  };

  const getOptionLabel = (option: Suggestion) =>
    option.type === 'airport' ? `${option.title} (${option.iata})` : option.optionLabel;
  const isOptionEqualToValue = (option: Suggestion, value: Suggestion) =>
    option.iata === value.iata && option.type === value.type;

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(async () => {
      if (selected && inputValue === selectedLabel) {
        setOptions([]);

        return;
      }

      if (inputValue.trim().length < MIN_QUERY_LENGTH) {
        setOptions([]);

        return;
      }

      setLoading(true);

      try {
        const next = await fetchSuggestions(inputValue);

        if (active) {
          setOptions(next);
        }
      } catch {
        if (active) {
          setOptions([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [inputValue, selected, selectedLabel]);

  return (
    <Autocomplete<Suggestion, false, false, false>
      autoHighlight
      componentsProps={{
        paper: { sx: { borderRadius: 2, mt: 1 } },
        popper: { sx: { '& .MuiAutocomplete-listbox': { p: 0.75 } } },
      }}
      filterOptions={values => values}
      getOptionDisabled={option => option.type === 'city'}
      getOptionLabel={getOptionLabel}
      id={name}
      inputValue={inputValue}
      isOptionEqualToValue={isOptionEqualToValue}
      loading={loading}
      noOptionsText={
        inputValue.trim().length < MIN_QUERY_LENGTH
          ? 'Type at least 2 characters to search'
          : 'No matching airports found'
      }
      onChange={handleLocationChange}
      onInputChange={handleInputChange}
      options={
        selected && !options.some(option => option.iata === selected.iata)
          ? [selected, ...options]
          : options
      }
      renderInput={params => (
        <TextField
          {...{
            ...params,
            InputProps: {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <FlightTakeoffIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
            inputProps: {
              ...params.inputProps,
              autoComplete: 'off',
              onBlur: event => {
                params.inputProps.onBlur?.(event as React.FocusEvent<HTMLInputElement>);
                setInputValue(selectedLabel);
              },
            },
          }}
          error={Boolean(errorHelperText?.length)}
          helperText={errorHelperText?.join('\n')}
          label={label}
          placeholder={placeholder}
          required={required}
        />
      )}
      renderOption={({ className, ...props }, option) => (
        <Box
          component="li"
          {...props}
          className={`${className ?? ''} itinerary-location-option itinerary-location-option--${option.type} itinerary-location-option--depth-${option.depth}`}
        >
          {option.type === 'city' ? <LocationCityIcon /> : <FlightTakeoffIcon />}
          <Typography noWrap>{option.optionLabel}</Typography>
          <Chip label={option.type === 'city' ? 'City' : 'Airport'} size="small" />
        </Box>
      )}
      value={selected}
    />
  );
}

export default ItineraryLocationSelector;
